import { FC, useCallback, useEffect, useState } from 'react';

import { PermissionCheck } from '../../components/PermissionCheck';

import { getOldQuery, getQuery } from '../../utilities/getQuery';

import { PageTitle } from '../../components/PageTitle';
import { TotalCasesChart } from '../../components/TotalCasesChart/TotalCasesChart';
import { useAppContext, useUserContext } from '../../context';
import { DashboardViewSwitch } from '../../components/DashboardViewSwitch/DashboardViewSwitch';
import { DateRangeSwitch } from '../../components/DateRangeSwitch/DateRangeSwitch';
import {
  CaseMixWidgetRow,
  MetadataWidgetRow,
  NonOperativeMetricWidget,
  ORAScoreWidget,
} from './Widgets';
import { useQueryDashboardNonOperativeMetrics } from '../../rest/query/useQueryDashboardNonOperativeMetrics';
import { useQueryDashboardDelayMetrics } from '../../rest/query/useQueryDashboardDelayMetrics';
import { useQueryDashboardCaseMix } from '../../rest/query/useQueryDashboardCaseMix';
import { routesPath } from '../../constants/routes';
import { useHistory } from '../../hooks/useHistory';
import { CompareValues } from '../../components/MultiFilter/MultiFilters.types';
import { genesisLabels } from '../../constants/analyticsLabels';
import { useQueryCasesMetrics } from '../../rest/query/useQueryCasesMetrics';
import classNames from 'clsx';
import { useQueryDashboardCaseHistory } from '../../rest/query/useQueryDashboardCaseHistory';
import { ChartDatesViews } from '../../components/TotalCasesChart/SelectChartDatesView';
import { BarChartDashboardSkeleton } from '../../components/BarChartDashboardSkeleton/BarChartDashboardSkeleton';
import { CopyClipboardButton } from '../../components/CopyClipboardButton';
import { makeCopyToClipboardHandler } from '../../utilities/makeCopyToClipboardHandler';
import { makeDownloadChartHandler } from '../../utilities/makeDownloadChartHandler';
import { DownloadButton } from '../../components/DownloadButton';

const getGenesisWidgetId = (genesisWidgetTitle: string): string => {
  return genesisWidgetTitle.replace(/ /g, '_').replace(/-/g, '_');
};

const nomWidgetBaseStyles = [
  'bg-white',
  'border',
  'shadow-sm',
  'w-[36rem]',
  'h-[17.4rem]',
  'rounded-main',
  'p-[1.6rem]',
];
const nomWidgetStylesClass = [
  ...nomWidgetBaseStyles,
  'mb-4',
  'lg:mb-12',
  'xl:mb-12',
  '2xl:mb-12',
  '2xl:ml-0',
];

export const Dashboard: FC = () => {
  const { data: userData } = useUserContext();
  const history = useHistory();
  const { presentationMode } = useAppContext();

  const copyToClipboard = useCallback(
    makeCopyToClipboardHandler('non-operative-metrics-group'),
    [],
  );
  const downloadWidgetImage = useCallback(
    makeDownloadChartHandler('non-operative-metrics-group'),
    [],
  );

  const params = new URLSearchParams(window.location.search);

  const [currentChartView, setCurrentChartView] = useState(ChartDatesViews[0].value);

  const changeChartView = useCallback((v: Periods) => {
    setCurrentChartView(v);
  }, []);

  const [isChartView, setIsChartView] = useState(false);

  const [prevQuery, setPrevQuery] = useState(getOldQuery(getQuery(history)));
  const [currentQuery, setCurrentQuery] = useState(getQuery(history));
  const [rangeType, setRangeType] = useState<string>(params.get('type') || 'all');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    const d = userData?.me.current?.organization?.availableData;

    if ((!type || type === 'all') && d?.from && d?.to) {
      setPrevQuery(getOldQuery({ from: d.from, to: d.to }));
      setCurrentQuery({ from: d.from, to: d.to });
    }
  }, [JSON.stringify(userData?.me.current?.organization?.availableData)]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setRangeType(params.get('type') || 'all');
  }, [currentQuery]);

  const isAll = rangeType === 'all';

  const { allFilters } = useQueryCasesMetrics({
    from: currentQuery.from,
    to: currentQuery.to,
  });

  const {
    nom: nomData,
    activity: activityData,
    loading: nomLoading,
  } = useQueryDashboardNonOperativeMetrics({
    from: currentQuery.from,
    to: currentQuery.to,
  });
  const { data: caseDataCurrent, loading: caseDataCurrentLoading } = useQueryDashboardCaseMix({
    from: currentQuery.from,
    to: currentQuery.to,
  });

  const { data: delayData, loading: delayLoading } = useQueryDashboardDelayMetrics({
    from: currentQuery.from,
    to: currentQuery.to,
  });

  // console.log("delayData", delayData)
  const { data: caseHistoryData, loading: caseHistoryLoading } = useQueryDashboardCaseHistory(
    {
      from: currentQuery.from,
      to: currentQuery.to,
      period: currentChartView,
    },
    true,
  );

  const { data: caseDataPrev, loading: caseDataPrevLoading } = useQueryDashboardCaseMix(
    {
      from: prevQuery.from,
      to: prevQuery.to,
    },
    !isAll,
  );

  const selectDates = useCallback((dates) => {
    setCurrentQuery(dates);
    setPrevQuery(getOldQuery(dates));
  }, []);

  const openCaseAnalytics = useCallback(
    (compare: CompareValues) => () => {
      const path = routesPath.casesAnalytics.replace('/:refId', '');
      history.push(
        `${path}?from=${currentQuery.from}&to=${currentQuery.to}&compare=${compare}&skipFilters=1`,
      );
    },
    [history],
  );

  const openSpecialtiesBubbleChart = useCallback(
    (phase: GenesisKeys) => (specialtyCode: SpecialtyCode) => {
      const compare: CompareValues = 'surgeon_id';
      const path = routesPath.nonOperativeMetrics.replace('/:refId', '');
      history.push(
        `${path}?from=${currentQuery.from}&to=${currentQuery.to}&compare=${compare}&skipFilters=1&specialties=${specialtyCode}&phase=${phase}&chartView=Bubble View`,
      );
    },
    [history],
  );

  const openNationalStatistics = useCallback(() => {
    history.push(
      routesPath.nonOperativeMetrics.replace('/:refId', '') +
        '?compare=national_statistics&skipFilters=1',
    );
  }, [history, currentQuery]);

  const changeDashboardView = useCallback((name: string) => {
    const isChart = name === 'Chart View';
    setIsChartView(isChart);

    const params = new URLSearchParams(window.location.search);
    params.set('dashboardView', name === 'Chart View' ? 'chart' : 'dashboard');
    history.replace({
      search: params.toString(),
    });
  }, []);

  const caseMixLoading = (caseDataPrevLoading && !isAll) || caseDataCurrentLoading;

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const dashboardChartPeriod = params.get('dashboardChartPeriod');
    const dashboardView = params.get('dashboardView');

    let shouldChangeUrl = false;

    if (dashboardChartPeriod && ChartDatesViews.find((v) => v.value === dashboardChartPeriod)) {
      changeChartView(dashboardChartPeriod as Periods);
    } else {
      params.set('dashboardChartPeriod', currentChartView);
      shouldChangeUrl = true;
    }

    if (dashboardView === 'dashboard' || dashboardView === 'chart') {
      setIsChartView(dashboardView === 'chart');
    } else {
      params.set('dashboardView', isChartView ? 'chart' : 'dashboard');
      shouldChangeUrl = true;
    }

    if (shouldChangeUrl) {
      history.replace({
        search: params.toString(),
      });
    }
  }, [isChartView]);

  const copyORAScoreWidgetToClipboard = useCallback(
    makeCopyToClipboardHandler('ORAScoreWidget'),
    [],
  );

  const downloadORAScoreWidgetImage = useCallback(makeDownloadChartHandler('ORAScoreWidget'), []);

  const copyCaseMixWidgetToClipboard = useCallback(makeCopyToClipboardHandler('CaseMixWidget'), []);
  const downloadCaseMixWidgetImage = useCallback(makeDownloadChartHandler('CaseMixWidget'), []);

  const copyMetadataWidgetToClipboard = useCallback(
    makeCopyToClipboardHandler('MetadataWidget'),
    [],
  );
  const downloadMetadataWidgetImage = useCallback(makeDownloadChartHandler('MetadataWidget'), []);

  const copyDelayMetricsToClipboard = useCallback(
    makeCopyToClipboardHandler('delay-metrics-group'),
    [],
  );
  const downloadDelayMetricsImage = useCallback(
    makeDownloadChartHandler('delay-metrics-group'),
    [],
  );

  const copyFCOTSWidgetToClipboard = useCallback(makeCopyToClipboardHandler('FCOTSWidget'), []);
  const downloadFCOTSWidgetImage = useCallback(makeDownloadChartHandler('FCOTSWidget'), []);

  const copyLCOTEWidgetToClipboard = useCallback(makeCopyToClipboardHandler('LCOTEWidget'), []);
  const downloadLCOTEWidgetImage = useCallback(makeDownloadChartHandler('LCOTEWidget'), []);

  return (
    <PermissionCheck check="readAnalytics" fallback={<div />}>
      <PageTitle header="Dashboard" subHeader={`Welcome Back, ${userData?.me?.fullName}`} />
      <div className="">
        <div className="flex justify-between mt-[3.5rem]">
          <div className="flex">
            <div className="mr-4">
              <DateRangeSwitch onSelect={selectDates} loading={caseMixLoading} />
            </div>
          </div>
          <div className="switch">
            <DashboardViewSwitch
              value={isChartView ? 'Chart View' : 'Dashboard View'}
              onChange={changeDashboardView}
            />
          </div>
        </div>

        {!isChartView && (
          <div className="flex flex-col xl:flex-row">
            <div className="flex flex-col justify-start pt-3">
              {!presentationMode && (
                <div className="lg:mr-12 mb-8 lg:mb-12 xl:mb-0 xl:mr-0 2xl:mr-16 group">
                  <div className="font-bold font-lato text-1620 mt-12 mb-2.5">
                    ORA Score
                    <span className="float-right -mt-2 -mr-0">
                      <div className="flex gap-2 w-1/12 opacity-0 group-hover:opacity-75 duration-500 group-hover:delay-200">
                        <DownloadButton
                          title="Download Image"
                          onClick={downloadORAScoreWidgetImage}
                        />
                        <CopyClipboardButton
                          title="Copy to Clipboard"
                          onClick={copyORAScoreWidgetToClipboard}
                        />
                      </div>
                    </span>
                  </div>
                  <div
                    className={classNames(nomWidgetBaseStyles, 'xl:mb-6', '2xl:mb-6')}
                    id="ORAScoreWidget"
                  >
                    <ORAScoreWidget
                      onClick={openNationalStatistics}
                      score={nomData?.ora_score || 0}
                      nationalStatistic={nomData?.national_statistic}
                    />
                  </div>
                </div>
              )}

              <div
                className={`flex flex-col lg:flex-row xl:flex-col justify-start ${
                  presentationMode && 'mt-12'
                }`}
              >
                <div className="lg:mr-12 xl:mr-0 2xl:mr-16 group">
                  <div className="font-bold font-lato text-1620 mb-3">
                    Case Mix
                    <span className="float-right -mt-2">
                      <div className="flex gap-2 w-1/12 opacity-0 group-hover:opacity-75 duration-500 group-hover:delay-200">
                        <DownloadButton
                          title="Download Image"
                          onClick={downloadCaseMixWidgetImage}
                        />
                        <CopyClipboardButton
                          title="Copy to Clipboard"
                          onClick={copyCaseMixWidgetToClipboard}
                        />
                      </div>
                    </span>
                  </div>
                  <div
                    id="CaseMixWidget"
                    onClick={openCaseAnalytics('modality')}
                    className="bg-white shadow-sm mb-8 lg:mb-2 2xl:mb-8 w-[36rem] h-[31.6rem] rounded-main p-[1.6rem] cursor-pointer"
                  >
                    <CaseMixWidgetRow
                      hideTrend={isAll}
                      type="total"
                      loading={caseMixLoading}
                      value={caseDataCurrent?.case_totals || 0}
                      prevValue={caseDataPrev?.case_totals || 0}
                    />
                    <CaseMixWidgetRow
                      hideTrend={isAll}
                      type="robotic"
                      loading={caseMixLoading}
                      value={caseDataCurrent?.frequencies?.Robotic || 0}
                      prevValue={caseDataPrev?.frequencies?.Robotic || 0}
                    />
                    <CaseMixWidgetRow
                      hideTrend={isAll}
                      type="open"
                      loading={caseMixLoading}
                      value={caseDataCurrent?.frequencies?.Open || 0}
                      prevValue={caseDataPrev?.frequencies?.Open || 0}
                    />
                    <CaseMixWidgetRow
                      hideTrend={isAll}
                      type="lap"
                      loading={caseMixLoading}
                      value={caseDataCurrent?.frequencies?.Lap || 0}
                      prevValue={caseDataPrev?.frequencies?.Lap || 0}
                    />
                    <CaseMixWidgetRow
                      hideTrend={isAll}
                      type="other"
                      loading={caseMixLoading}
                      value={caseDataCurrent?.frequencies?.Other || 0}
                      prevValue={caseDataPrev?.frequencies?.Other || 0}
                    />
                  </div>
                </div>
                <div className="2xl:mr-16 group">
                  <div className="font-bold font-lato text-1620 xl:mt-4 mb-[0.6rem]">
                    Metadata
                    <span className="float-right -mt-2">
                      <div className="flex gap-2 w-1/12 opacity-0 group-hover:opacity-75 duration-500 group-hover:delay-200">
                        <DownloadButton
                          title="Download Image"
                          onClick={downloadMetadataWidgetImage}
                        />
                        <CopyClipboardButton
                          title="Copy to Clipboard"
                          onClick={copyMetadataWidgetToClipboard}
                        />
                      </div>
                    </span>
                  </div>
                  <div
                    id="MetadataWidget"
                    className="bg-white shadow-sm w-[36rem] h-[26.6rem] rounded-main p-[1.6rem] xl:mt-0"
                  >
                    <MetadataWidgetRow
                      allFilters={allFilters}
                      type="ors"
                      onClick={openCaseAnalytics('OR')}
                      loading={caseDataCurrentLoading}
                      options={caseDataCurrent?.metadata?.ORs?.map((item) => ({
                        label: item,
                        value: item,
                      }))}
                    />
                    <MetadataWidgetRow
                      allFilters={allFilters}
                      type="specialties"
                      onClick={openCaseAnalytics('specialty_code')}
                      loading={caseDataCurrentLoading}
                      options={caseDataCurrent?.metadata?.specialties?.map((item) => ({
                        label: item,
                        value: item,
                      }))}
                    />
                    <MetadataWidgetRow
                      allFilters={allFilters}
                      type="procedures"
                      onClick={openCaseAnalytics('procedure')}
                      loading={caseDataCurrentLoading}
                      options={caseDataCurrent?.metadata?.procedures?.map((item) => ({
                        label: item,
                        value: item,
                      }))}
                    />
                    <MetadataWidgetRow
                      allFilters={allFilters}
                      type="surgeons"
                      onClick={openCaseAnalytics('surgeon_id')}
                      loading={caseDataCurrentLoading}
                      options={caseDataCurrent?.metadata?.surgeons?.map((item) => ({
                        label: item,
                        value: item,
                      }))}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 w-full 2xl:-ml-8">
              <div className="flex group">
                <div className="font-bold font-lato text-1620 xl:mb-2.5 2xl:mb-2.5 xl:mr-0 lg:mr-12 xl:ml-14 2xl:ml-9 mb-3.5">
                  Delay Metrics
                </div>
                <div className="flex w-3/12 opacity-0 group-hover:opacity-75 duration-500 group-hover:delay-200 justify-start gap-2 ml-5 -mt-1 mb-3">
                  <div>
                    {downloadDelayMetricsImage && (
                      <DownloadButton
                        title="Download Delay Metrics"
                        onClick={downloadDelayMetricsImage}
                      />
                    )}
                  </div>
                  <div>
                    {copyDelayMetricsToClipboard && (
                      <CopyClipboardButton
                        title="Copy Delay Metrics to Clipboard"
                        onClick={copyDelayMetricsToClipboard}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="flex xl:justify-start 2xl:justify-start flex-wrap lg:w-full 2xl:ml-0 mb-8">
                <span className="flex" id="delay-metrics-group">
                  <div className="lg:px-10 xl:px-8 lg:pl-0 xl:pl-14 2xl:pl-8">
                    <div
                      id="FCOTSWidget"
                      className="bg-white border shadow-[0px_5px_8px_rgba(12,45,202,0.05)] w-[36rem] h-[17.4rem] rounded-main p-[1.6rem] relative group"
                    >
                      {delayData.fcots.count !== null && (
                        <div className="absolute bottom-6 left-6 text-gray-700 text-1214 font-normal px-3 py-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-20 bg-gray-200">
                          Based on {delayData.fcots.count} first cases
                        </div>
                      )}
                      <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-75 duration-500 group-hover:delay-200">
                        <DownloadButton title="Download Image" onClick={downloadFCOTSWidgetImage} />
                        <CopyClipboardButton
                          title="Copy to Clipboard"
                          onClick={copyFCOTSWidgetToClipboard}
                        />
                      </div>
                      <div className="border-b pb-[1.6rem]">
                        <div className="font-bold font-lato text-1620 uppercase">
                          First Case On Time Start
                        </div>
                      </div>
                      <div className="flex justify-between mt-6">
                        <div>
                          <div className="flex items-baseline mb-2">
                            {delayLoading ? (
                              <div className="bg-ghost animate-pulse rounded-lg w-[5rem] h-[3.2rem] mr-1" />
                            ) : (
                              <div className="text-3232 font-lato font-bold mr-1">
                                {delayData.fcots.onTimePercent !== null
                                  ? `${delayData.fcots.onTimePercent}%`
                                  : 'N/A'}
                              </div>
                            )}
                          </div>
                          <div className="font-semibold font-lato text-1220 text-osloGray">
                            On time start
                          </div>
                        </div>
                        <div className="flex flex-col justify-start items-end">
                          <div className="font-bold font-lato text-1220 mb-2">Average Delay</div>
                          <div className="flex items-baseline mb-2">
                            {delayLoading ? (
                              <div className="bg-ghost animate-pulse rounded-lg w-[4rem] h-[2rem] mr-1" />
                            ) : (
                              <>
                                <div className="text-2020 font-lato font-bold mr-1">
                                  {delayData.fcots.avgDelayMin !== null
                                    ? delayData.fcots.avgDelayMin
                                    : 'N/A'}
                                </div>
                                {delayData.fcots.avgDelayMin !== null && (
                                  <div className="text-1214 font-lato font-bold text-osloGray">
                                    min
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          {!delayLoading && delayData.fcots.sdDelayMin !== null && (
                            <div className="w-fit flex font-bold font-lato text-1420 text-headerTextGrey bg-peranoHover py-0.5 px-2 rounded-lg">
                              +/- {delayData.fcots.sdDelayMin}
                            </div>
                          )}
                          {delayLoading && (
                            <div className="bg-peranoHover animate-pulse rounded-lg w-[5rem] h-[2.2rem]" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="lg:px-10 xl:px-6 lg:pl-0">
                    <div
                      id="LCOTEWidget"
                      className="bg-white border shadow-[0px_5px_8px_rgba(12,45,202,0.05)] w-[36rem] h-[17.4rem] rounded-main p-[1.6rem] relative group"
                    >
                      {delayData.lcote.count !== null && (
                        <div className="absolute bottom-6 left-6 text-gray-700 text-1214 font-normal px-3 py-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-20 bg-gray-200">
                          Based on {delayData.lcote.count} last cases
                        </div>
                      )}
                      <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-75 duration-500 group-hover:delay-200">
                        <DownloadButton title="Download Image" onClick={downloadLCOTEWidgetImage} />
                        <CopyClipboardButton
                          title="Copy to Clipboard"
                          onClick={copyLCOTEWidgetToClipboard}
                        />
                      </div>
                      <div className="border-b pb-[1.6rem]">
                        <div className="font-bold font-lato text-1620 uppercase">
                          Last Case On Time End
                        </div>
                      </div>
                      <div className="flex justify-between mt-6">
                        <div>
                          <div className="flex items-baseline mb-2">
                            {delayLoading ? (
                              <div className="bg-ghost animate-pulse rounded-lg w-[5rem] h-[3.2rem] mr-1" />
                            ) : (
                              <div className="text-3232 font-lato font-bold mr-1">
                                {delayData.lcote.endDelayPercent !== null
                                  ? `${delayData.lcote.endDelayPercent}%`
                                  : 'N/A'}
                              </div>
                            )}
                          </div>
                          <div className="font-semibold font-lato text-1220 text-osloGray">
                            On time end
                          </div>
                        </div>
                        <div className="flex flex-col justify-start items-end">
                          <div className="font-bold font-lato text-1220 mb-2">Average Delay</div>
                          <div className="flex items-baseline mb-2">
                            {delayLoading ? (
                              <div className="bg-ghost animate-pulse rounded-lg w-[4rem] h-[2rem] mr-1" />
                            ) : (
                              <>
                                <div className="text-2020 font-lato font-bold mr-1">
                                  {delayData.lcote.avgDelayMin !== null
                                    ? delayData.lcote.avgDelayMin
                                    : 'N/A'}
                                </div>
                                {delayData.lcote.avgDelayMin !== null && (
                                  <div className="text-1214 font-lato font-bold text-osloGray">
                                    min
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          {!delayLoading && delayData.lcote.sdDelayMin !== null && (
                            <div className="w-fit flex font-bold font-lato text-1420 text-headerTextGrey bg-peranoHover py-0.5 px-2 rounded-lg">
                              +/- {delayData.lcote.sdDelayMin}
                            </div>
                          )}
                          {delayLoading && (
                            <div className="bg-peranoHover animate-pulse rounded-lg w-[5rem] h-[2.2rem]" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </span>
              </div>

              <div className="flex group">
                <div className="font-bold font-lato text-1620 xl:mb-2.5 2xl:mb-2.5 xl:mr-0 lg:mr-12 xl:ml-14 2xl:ml-9 mb-3.5">
                  Non-Operative Metrics
                </div>
                <div className="flex w-3/12 opacity-0 group-hover:opacity-75 duration-500 group-hover:delay-200 justify-start gap-2 ml-5 -mt-1 mb-3">
                  <div>
                    {downloadWidgetImage && (
                      <DownloadButton
                        title="Download Non-Operative Metrics"
                        onClick={downloadWidgetImage}
                      />
                    )}
                  </div>
                  <div>
                    {copyToClipboard && (
                      <CopyClipboardButton
                        title="Copy Non-Operative Metrics to Clipboard"
                        onClick={copyToClipboard}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="flex xl:justify-start 2xl:justify-start flex-wrap lg:w-full 2xl:ml-0 ">
                <span className="flex" id="non-operative-metrics-group">
                  <div className="nom-1-column grid lg:gap-0 xl:gap-4 lg:px-10 xl:px-8 lg:pl-0 xl:pl-14 2xl:pl-8">
                    <div
                      id={getGenesisWidgetId(genesisLabels.surgery_surgery)}
                      className={classNames(nomWidgetStylesClass)}
                    >
                      <NonOperativeMetricWidget
                        onNationalStatisticClick={openNationalStatistics}
                        onSpecialtyClick={openSpecialtiesBubbleChart('surgery_surgery')}
                        specialties={allFilters.specialties}
                        title={genesisLabels.surgery_surgery}
                        loading={nomLoading}
                        topSpecialties={nomData?.surgery_surgery}
                        nationalAvg={nomData?.national_statistic?.surgery_surgery?.average}
                        avg={activityData?.surgery_surgery?.means?.[0]}
                        standardDeviation={activityData?.surgery_surgery?.sds?.[0]}
                      />
                    </div>

                    <div
                      className={classNames(nomWidgetStylesClass)}
                      id={getGenesisWidgetId(genesisLabels.sterileprep_patientin)}
                    >
                      <NonOperativeMetricWidget
                        onNationalStatisticClick={openNationalStatistics}
                        onSpecialtyClick={openSpecialtiesBubbleChart('sterileprep_patientin')}
                        specialties={allFilters.specialties}
                        title={genesisLabels.sterileprep_patientin}
                        topSpecialties={nomData?.sterileprep_patientin}
                        loading={nomLoading}
                        nationalAvg={nomData?.national_statistic?.sterileprep_patientin?.average}
                        avg={activityData?.sterileprep_patientin?.means?.[0]}
                        standardDeviation={activityData?.sterileprep_patientin?.sds?.[0]}
                      />
                    </div>
                    <div
                      className={classNames(nomWidgetStylesClass)}
                      id={getGenesisWidgetId(genesisLabels.patientin_firstcut)}
                    >
                      <NonOperativeMetricWidget
                        onNationalStatisticClick={openNationalStatistics}
                        onSpecialtyClick={openSpecialtiesBubbleChart('patientin_firstcut')}
                        specialties={allFilters.specialties}
                        title={genesisLabels.patientin_firstcut}
                        loading={nomLoading}
                        topSpecialties={nomData?.patientin_firstcut}
                        nationalAvg={nomData?.national_statistic?.patientin_firstcut?.average}
                        avg={activityData?.patientin_firstcut?.means?.[0]}
                        standardDeviation={activityData?.patientin_firstcut?.sds?.[0]}
                      />
                    </div>
                  </div>
                  <div className="nom-2-column grid lg:gap-0 xl:gap-4 lg:px-10 xl:px-6 lg:pl-0">
                    <div
                      id={getGenesisWidgetId(genesisLabels.patientout_patientin)}
                      className={classNames(nomWidgetStylesClass)}
                    >
                      <NonOperativeMetricWidget
                        onNationalStatisticClick={openNationalStatistics}
                        onSpecialtyClick={openSpecialtiesBubbleChart('patientout_patientin')}
                        specialties={allFilters.specialties}
                        title={genesisLabels.patientout_patientin}
                        loading={nomLoading}
                        topSpecialties={nomData?.patientout_patientin}
                        nationalAvg={nomData?.national_statistic?.patientout_patientin?.average}
                        avg={activityData?.patientout_patientin?.means?.[0]}
                        standardDeviation={activityData?.patientout_patientin?.sds?.[0]}
                      />
                    </div>
                    <div
                      className={classNames(nomWidgetStylesClass)}
                      id={getGenesisWidgetId(genesisLabels.patientclose_patientout)}
                    >
                      <NonOperativeMetricWidget
                        onNationalStatisticClick={openNationalStatistics}
                        onSpecialtyClick={openSpecialtiesBubbleChart('patientclose_patientout')}
                        specialties={allFilters.specialties}
                        title={genesisLabels.patientclose_patientout}
                        loading={nomLoading}
                        topSpecialties={nomData?.patientclose_patientout}
                        nationalAvg={nomData?.national_statistic?.patientclose_patientout?.average}
                        avg={activityData?.patientclose_patientout?.means?.[0]}
                        standardDeviation={activityData?.patientclose_patientout?.sds?.[0]}
                      />
                    </div>

                    <div
                      className={classNames(nomWidgetStylesClass)}
                      id={getGenesisWidgetId(genesisLabels.patientout_sterileprep)}
                    >
                      <NonOperativeMetricWidget
                        onNationalStatisticClick={openNationalStatistics}
                        onSpecialtyClick={openSpecialtiesBubbleChart('patientout_sterileprep')}
                        specialties={allFilters.specialties}
                        title={genesisLabels.patientout_sterileprep}
                        loading={nomLoading}
                        topSpecialties={nomData?.patientout_sterileprep}
                        nationalAvg={nomData?.national_statistic?.patientout_sterileprep?.average}
                        avg={activityData?.patientout_sterileprep?.means?.[0]}
                        standardDeviation={activityData?.patientout_sterileprep?.sds?.[0]}
                      />
                    </div>
                  </div>
                </span>
              </div>
            </div>
          </div>
        )}
        {isChartView && (
          <div className="bg-white mt-12">
            {caseHistoryLoading && <BarChartDashboardSkeleton />}

            {!caseHistoryLoading && (
              <TotalCasesChart
                data={caseHistoryData}
                view={currentChartView}
                loading={caseHistoryLoading}
                onChangeView={changeChartView}
              />
            )}
          </div>
        )}
      </div>
    </PermissionCheck>
  );
};
